/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import { Component, Vue } from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import {UgcItemInterface} from '@/store/modules/ugc-item/types';
import BuildThumbnail from '@/components/BuildThumbnail/scripts';

const namespaceUgcItem: string = 'ugcItemModule';
const namespaceSettings: string = 'settingsModule';

/**
 * Item list component.
 *
 * This represents a list of user items that have been added to the system.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
@Component({
    name: 'c-item-list',
    components: {
        'c-build-thumbnail': BuildThumbnail,
    },
})
export default class ItemList extends Vue {
    @Getter('items', {namespace: namespaceUgcItem}) public items!: UgcItemInterface[];
    @Getter('hasItems', {namespace: namespaceUgcItem}) public hasItems!: boolean;
    @Getter('hasMedia', {namespace: namespaceSettings}) public hasMedia!: (name: string) => boolean;
    @Action('deleteItem', {namespace: namespaceUgcItem}) public deleteItem: any;

    public sortedItems: UgcItemInterface[] = [];
    public sortByAsc: boolean = false;
    public sortByField!: string;

    /**
     * Created lifecycle event.
     */
    public created(): void {
        this.sortedItems = this.sortItems('updatedAt');
    }

    /**
     * Find the sort class for field.
     *
     * @param {string} field
     *
     * @returns {any}
     */
    public getSortClass(field: string): any {
        return {
            ascending: field === this.sortByField && this.sortByAsc,
            descending: field === this.sortByField && !this.sortByAsc,
        };
    }

    /**
     * Comparison function for array sort.
     *
     * @param {string | number} a
     * @param {string | number} b
     *
     * @returns {number}
     */
    public sortCompare(a: string|number, b: string|number): number {
        if (a > b) {
            return 1;
        }

        if (a < b) {
            return -1;
        }

        return 0;
    }

    /**
     * Sort the items by field (in direction).
     *
     * @param {string} field
     * @param {boolean} asc
     * @returns {UgcItemInterface[]}
     */
    public sortItems(field: string): UgcItemInterface[] {
        this.sortByAsc = this.sortByField === field ? !this.sortByAsc : false;
        this.sortByField = field;

        switch (field) {
            case 'createdAt':
            case 'updatedAt':
                return this.items.sort((a: UgcItemInterface, b: UgcItemInterface): number => {
                    return (this.sortByAsc ? 1 : -1) * this.sortCompare(a[field], b[field]);
                });
            case 'name':
            case 'status':
                return this.items.sort((a: UgcItemInterface, b: UgcItemInterface): number => {
                    return (this.sortByAsc ? -1 : 1) * this.sortCompare(a[field].toUpperCase(), b[field].toUpperCase());
                });
        }

        return this.items;
    }

    /**
     * Remove item action event.
     *
     * @param {UgcItemInterface} item
     *
     * @returns {Promise<void>}
     */
    public async removeItem(item: UgcItemInterface): Promise<void> {
        this.$modal.show('dialog', {
            title: `<h2>${this.$i18n.translate('modal.delete.item.title', {})}</h2>`,
            text: this.$i18n.translate('modal.delete.item.text', {name: item.name}),
            buttons: [
                {
                    title: `<span role="confirm">${this.$i18n.translate('modal.delete.item.confirm', {})}</span>`,
                    handler: (): void => {
                        this.$modal.hide('dialog');
                        this.actionRemoveItem(item);
                    },
                },
                {
                    title: `<span role="cancel">${this.$i18n.translate('modal.delete.item.cancel', {})}</span>`,
                    default: true,
                },
            ],
        });
    }

    /**
     * Remove item action event.
     *
     * @param {UgcItemInterface} item
     *
     * @returns {Promise<void>}
     */
    private async actionRemoveItem(item: UgcItemInterface): Promise<void> {
        try {
            await this.deleteItem(item);
            this.$toastr.success(this.$i18n.translate('notification.item.deletion.success', {}) || '');
        } catch (e) {
            this.$toastr.error(this.$i18n.translate('notification.item.deletion.failed', {}) || '');
        }
    }
}

